import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProfessorTheme } from 'src/app/_model/models';
import { ThemeService, PRESET_THEMES } from 'src/app/_services/theme.service';


@Component({
  selector: 'tangram-theme-customizer-modal',
  templateUrl: './theme-customizer-modal.component.html',
  styleUrls: ['./theme-customizer-modal.component.scss'],
})
export class ThemeCustomizerModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('previewRef', { static: false }) previewRef: ElementRef<HTMLElement>;
  /** Passed in by the caller — the palette to edit. */
  theme: Partial<ProfessorTheme> = {};

  /** Passed in by the caller — existing theme ID (empty = create new). */
  themeId: string = '';

  /** Local working copy so we can cancel without side-effects. */
  colors: Partial<ProfessorTheme> = {};

  /** Name used when saving/updating. */
  themeName: string = '';

  presets = Object.values(PRESET_THEMES);
  selectedPresetLabel: string = '';

  saving: boolean = false;
  saveError: string = '';

  /** Snapshot of CSS vars before the modal opened — restored on cancel. */
  private _snapshot: [string, string][] = [];
  /** Snapshot of data-tangram-mode before the modal opened. */
  private _modeSnapshot: string | null = null;
  /** Snapshot of data-tangram-theme before the modal opened. */
  private _themeAttrSnapshot: string | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private themeService: ThemeService,
  ) {}

  /** True when editing an existing theme; false when creating a new one. */
  get isEditMode(): boolean {
    return !!this.themeId;
  }

  ngOnInit(): void {
    // Capture current CSS var state so we can restore on cancel
    const root = document.documentElement;
    const varNames = [
      '--tangram-bg', '--tangram-surface', '--tangram-text',
      '--tangram-header-bg', '--tangram-header-text', '--tangram-primary',
      '--tangram-secondary', '--tangram-accent', '--tangram-button', '--tangram-panel',
    ];
    this._snapshot = varNames.map(function(v) {
      return [v, root.style.getPropertyValue(v)] as [string, string];
    });
    this._modeSnapshot = root.getAttribute('data-tangram-mode');
    this._themeAttrSnapshot = root.getAttribute('data-tangram-theme');

    // Seed working copy from the passed-in theme (pre-fills colors on edit).
    // buttonColor is kept in sync with primaryButtonColor — the UI exposes a
    // single "Primary / Button Color" picker and both CSS vars must match.
    const primaryBtn = this.theme.primaryButtonColor || this.theme.buttonColor || '#3737CD';
    this.colors = {
      primaryButtonColor: primaryBtn,
      secondaryColor:     this.theme.secondaryColor     || '#1E1E6E',
      accentColor:        this.theme.accentColor        || '#7C7CFF',
      backgroundColor:    this.theme.backgroundColor    || '#FFFFFF',
      textColor:          this.theme.textColor          || '#111111',
      buttonColor:        primaryBtn,
      headerBackground:   this.theme.headerBackground   || '#3737CD',
      headerTextColor:    this.theme.headerTextColor    || '#FFFFFF',
      surfaceColor:       this.theme.surfaceColor       || '#F5F5F5',
      cardBackground:     this.theme.cardBackground     || '#F5F5F5',
    };

    // Pre-fill name when editing an existing theme
    this.themeName = this.theme.name || '';

    // Apply existing colors as live preview immediately on edit
    if (this.isEditMode) {
      this.themeService.applyColorsLive(this.colors);
    }
  }

  ngAfterViewInit(): void {
    this._syncPreviewVars();
  }

  ngOnDestroy(): void {}

  // ── Preset selection ───────────────────────────────────────────────────────

  applyPreset(preset: typeof PRESET_THEMES[string]): void {
    this.selectedPresetLabel = preset.label;
    const palette: Partial<ProfessorTheme> = {};
    Object.keys(preset).forEach(key => {
      if (key !== 'label') { palette[key] = preset[key]; }
    });
    Object.assign(this.colors, palette);
    this.colors.buttonColor = this.colors.primaryButtonColor;
    this.themeService.applyColorsLive(this.colors);
    this._syncPreviewVars();
  }

  // ── Live preview as user adjusts a picker ─────────────────────────────────

  onColorChange(): void {
    // Keep --tangram-button in sync with --tangram-primary so all btn-primary
    // elements across the app reflect the selected color immediately.
    this.colors.buttonColor = this.colors.primaryButtonColor;
    this.themeService.applyColorsLive(this.colors);
    this._syncPreviewVars();
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * In create mode: creates a new theme and sets it as the active default.
   * In edit mode: updates the existing theme (no default change).
   */
  apply(): void {
    if (this.isEditMode) {
      this._updateTheme();
    } else {
      this._createAndPersist();
    }
  }

  /**
   * Save Template (create) / Update Theme (edit) — single save entry-point.
   */
  saveTemplate(): void {
    if (this.isEditMode) {
      this._updateTheme();
    } else {
      this._createAndPersist();
    }
  }

  // ── Create flow ────────────────────────────────────────────────────────────

  private _createAndPersist(): void {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    const name = this.themeName || this._generateDefaultName();
    const payload: Partial<ProfessorTheme> = Object.assign({}, this.colors, { name });

    this.themeService.createTheme(payload).subscribe(
      (res: any) => {
        const created = res && res.data;
        const newId = created && created._id;
        if (newId) {
          this.themeService.setDefaultTheme(newId).subscribe({
            next: () => {
              this.saving = false;
              this.activeModal.close({ saveToApi: true, colors: this.colors, name, newThemeId: newId });
            },
            error: () => {
              this.saving = false;
              this.activeModal.close({ saveToApi: true, colors: this.colors, name, newThemeId: newId });
            },
          });
        } else {
          this.saving = false;
          this.activeModal.close({ saveToApi: true, colors: this.colors, name });
        }
      },
      () => {
        this.saving = false;
        this.saveError = 'Failed to save theme. Please try again.';
      }
    );
  }

  // ── Edit / update flow ─────────────────────────────────────────────────────

  private _updateTheme(): void {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    const name = this.themeName || this._generateDefaultName();
    const payload: Partial<ProfessorTheme> = Object.assign({}, this.colors, { name });

    this.themeService.updateTheme(this.themeId, payload).subscribe({
      next: () => {
        this.saving = false;
        this.activeModal.close({ updated: true, colors: this.colors, name, themeId: this.themeId });
      },
      error: () => {
        this.saving = false;
        this.saveError = 'Failed to update theme. Please try again.';
      },
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private _generateDefaultName(): string {
    const d = new Date();
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    return `Custom ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  resetToDefault(): void {
    this.selectedPresetLabel = 'Ocean Blue';
    const preset = PRESET_THEMES['OCEAN_BLUE'];
    const palette: Partial<ProfessorTheme> = {};
    Object.keys(preset).forEach(key => {
      if (key !== 'label') { palette[key] = preset[key]; }
    });
    Object.assign(this.colors, palette);
    this.colors.buttonColor = this.colors.primaryButtonColor;
    this.themeService.applyColorsLive(this.colors);
    this._syncPreviewVars();
  }

  // ── Sync CSS custom properties to the preview container ───────────────────

  private _syncPreviewVars(): void {
    if (!this.previewRef) { return; }
    const el = this.previewRef.nativeElement;
    el.style.setProperty('--pv-bg',          this.colors.backgroundColor    || '#FFFFFF');
    el.style.setProperty('--pv-text',         this.colors.textColor          || '#111111');
    el.style.setProperty('--pv-header-bg',    this.colors.headerBackground   || '#3737CD');
    el.style.setProperty('--pv-header-text',  this.colors.headerTextColor    || '#FFFFFF');
    el.style.setProperty('--pv-primary',      this.colors.primaryButtonColor || '#3737CD');
    el.style.setProperty('--pv-secondary',    this.colors.secondaryColor     || '#1E1E6E');
    el.style.setProperty('--pv-accent',       this.colors.accentColor        || '#7C7CFF');
  }

  cancel(): void {
    // Restore the CSS vars + mode/theme attributes that were active before the
    // modal opened, so dismissing fully rolls back the live preview.
    const root = document.documentElement;
    this._snapshot.forEach(function(pair) {
      if (pair[1]) {
        root.style.setProperty(pair[0], pair[1]);
      } else {
        root.style.removeProperty(pair[0]);
      }
    });
    if (this._modeSnapshot) {
      root.setAttribute('data-tangram-mode', this._modeSnapshot);
    } else {
      root.removeAttribute('data-tangram-mode');
    }
    if (this._themeAttrSnapshot) {
      root.setAttribute('data-tangram-theme', this._themeAttrSnapshot);
    } else {
      root.removeAttribute('data-tangram-theme');
    }
    this.activeModal.dismiss();
  }
}
